import type { ComponentKind } from "@/lib/pipe-spec";

export interface Pt {
  x: number;
  y: number;
}

/**
 * Single-line ISO symbols for in-line components.
 * `a` and `b` are the screen-space ends of the component body; the symbol is
 * drawn between them, oriented along the pipe.
 */
export default function ComponentSymbol({
  kind,
  a,
  b,
  scale = 1,
  ghost = false,
  label,
}: {
  kind: ComponentKind;
  a: Pt;
  b: Pt;
  /** marker size multiplier (bigger on touch) */
  scale?: number;
  ghost?: boolean;
  label?: string;
}) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l = Math.hypot(dx, dy) || 1;
  const ux = dx / l;
  const uy = dy / l;
  const px = -uy;
  const py = ux;
  const c = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  // symbol half-height, and a minimum body length so tiny valves stay readable
  const h = 6 * scale;
  const half = Math.max(l / 2, 5 * scale);
  const e1 = { x: c.x - ux * half, y: c.y - uy * half };
  const e2 = { x: c.x + ux * half, y: c.y + uy * half };
  const off = (p: Pt, s: number) => ({ x: p.x + px * s, y: p.y + py * s });

  const stroke = ghost ? "hsl(var(--primary))" : "hsl(var(--foreground))";
  const fill = "hsl(var(--background))";
  const common = {
    stroke,
    strokeWidth: 1.6 * scale,
    fill: "none" as const,
    opacity: ghost ? 0.75 : 1,
  };

  /** classic bow-tie valve body */
  const bowtie = (
    <path
      d={`M ${off(e1, -h).x} ${off(e1, -h).y} L ${off(e1, h).x} ${off(e1, h).y} L ${
        off(e2, -h).x
      } ${off(e2, -h).y} L ${off(e2, h).x} ${off(e2, h).y} Z`}
      {...common}
      fill={fill}
    />
  );

  const flangeTick = (p: Pt) => (
    <line
      x1={off(p, -h * 1.15).x}
      y1={off(p, -h * 1.15).y}
      x2={off(p, h * 1.15).x}
      y2={off(p, h * 1.15).y}
      {...common}
    />
  );

  const stem = (
    <line
      x1={c.x}
      y1={c.y}
      x2={off(c, -h * 1.9).x}
      y2={off(c, -h * 1.9).y}
      {...common}
      strokeWidth={1.2 * scale}
    />
  );

  let body: JSX.Element;
  switch (kind) {
    case "gate":
      body = (
        <>
          {bowtie}
          {stem}
          <line
            x1={off(c, -h * 1.9).x - px * 0}
            y1={off(c, -h * 1.9).y}
            x2={off(c, -h * 1.9).x + ux * h}
            y2={off(c, -h * 1.9).y + uy * h}
            {...common}
            strokeWidth={1.2 * scale}
          />
          <line
            x1={off(c, -h * 1.9).x - ux * h}
            y1={off(c, -h * 1.9).y - uy * h}
            x2={off(c, -h * 1.9).x}
            y2={off(c, -h * 1.9).y}
            {...common}
            strokeWidth={1.2 * scale}
          />
          {flangeTick(e1)}
          {flangeTick(e2)}
        </>
      );
      break;
    case "ball":
      body = (
        <>
          {bowtie}
          <circle cx={c.x} cy={c.y} r={h * 0.5} fill={stroke} stroke="none" opacity={common.opacity} />
          {stem}
          {flangeTick(e1)}
          {flangeTick(e2)}
        </>
      );
      break;
    case "globe":
      body = (
        <>
          {bowtie}
          <circle cx={c.x} cy={c.y} r={h * 0.55} {...common} fill={fill} strokeWidth={1.2 * scale} />
          {stem}
          {flangeTick(e1)}
          {flangeTick(e2)}
        </>
      );
      break;
    case "check":
      body = (
        <>
          {bowtie}
          {/* flapper leaning against the seat = direction of flow */}
          <line
            x1={off(e1, h).x}
            y1={off(e1, h).y}
            x2={off(e2, -h * 0.2).x}
            y2={off(e2, -h * 0.2).y}
            {...common}
            strokeWidth={1.2 * scale}
          />
          {flangeTick(e1)}
          {flangeTick(e2)}
        </>
      );
      break;
    case "butterfly":
      body = (
        <>
          <circle cx={c.x} cy={c.y} r={h} {...common} fill={fill} />
          <line
            x1={c.x - ux * h * 0.75 - px * h * 0.75}
            y1={c.y - uy * h * 0.75 - py * h * 0.75}
            x2={c.x + ux * h * 0.75 + px * h * 0.75}
            y2={c.y + uy * h * 0.75 + py * h * 0.75}
            {...common}
            strokeWidth={1.2 * scale}
          />
          {flangeTick(e1)}
          {flangeTick(e2)}
        </>
      );
      break;
    case "flangePair":
      body = (
        <>
          {flangeTick(e1)}
          {flangeTick(e2)}
          <line x1={e1.x} y1={e1.y} x2={e2.x} y2={e2.y} {...common} />
        </>
      );
      break;
    case "blindFlange":
      body = (
        <>
          {flangeTick(e1)}
          <path
            d={`M ${off(e1, -h * 1.15).x} ${off(e1, -h * 1.15).y} L ${off(e2, -h * 1.15).x} ${
              off(e2, -h * 1.15).y
            } L ${off(e2, h * 1.15).x} ${off(e2, h * 1.15).y} L ${off(e1, h * 1.15).x} ${
              off(e1, h * 1.15).y
            } Z`}
            fill={stroke}
            stroke="none"
            opacity={common.opacity}
          />
        </>
      );
      break;
    case "union":
      body = (
        <>
          {flangeTick(e1)}
          {flangeTick(c)}
          {flangeTick(e2)}
        </>
      );
      break;
    case "strainer":
    default:
      body = (
        <>
          {bowtie}
          <line
            x1={c.x}
            y1={c.y}
            x2={off(c, h * 1.9).x + ux * h}
            y2={off(c, h * 1.9).y + uy * h}
            {...common}
            strokeWidth={1.2 * scale}
          />
          {flangeTick(e1)}
          {flangeTick(e2)}
        </>
      );
      break;
  }

  return (
    <g>
      {body}
      {label && (
        <text
          x={off(c, -h * 2.6).x}
          y={off(c, -h * 2.6).y}
          textAnchor="middle"
          className="fill-primary"
          style={{ fontSize: 9 * scale, fontFamily: "ui-monospace, monospace" }}
        >
          {label}
        </text>
      )}
    </g>
  );
}
